import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, Save, Loader2, User, Mail, Building2, Plus, Trash2, Upload, Check } from "lucide-react";
import LeadFileActions from "../components/LeadFileActions";

/**
 * KYC Information — port of Kinntegra-Old client-kyc-info-{profile,communication,bankdetails}
 * for each family member captured in the Introduction stage.
 *
 * Fields lifted verbatim from:
 *   /tmp/kinntegra-old/kinntegra-webapp/src/app/views/client-kyc-info-profile
 *   /tmp/kinntegra-old/kinntegra-webapp/src/app/views/client-kyc-info-communication
 *   /tmp/kinntegra-old/kinntegra-webapp/src/app/views/client-kyc-info-bankdetails
 */

const GENDERS = ["Male", "Female", "Other"];
const OCCUPATIONS = ["Salaried", "Business", "Professional", "Retired", "Housewife", "Student", "Agriculture", "Other"];
const INCOME_SLABS = ["Below 1 Lakh", "1 - 5 Lakh", "5 - 10 Lakh", "10 - 25 Lakh", "25 Lakh - 1 Cr", "Above 1 Cr"];
const WEALTH_SOURCES = ["Salary", "Business Income", "Inheritance", "Investments", "Other"];
const KYC_STATUSES = ["KYC Registered", "KYC Verified", "KYC Hold", "New", "Not Available"];
const ADDRESS_TYPES = ["Residential", "Business", "Registered Office", "Other"];
const BANK_ACCOUNT_TYPES = ["Savings", "Current", "NRE", "NRO", "FCNR"];

const blankProfile = () => ({
  pan: "", aadhaar: "", ckyc: "",
  gender: "", place_of_birth: "", country_of_birth: "India",
  occupation: "", employer_name: "",
  gross_annual_income: "", net_worth: "", net_worth_date: "",
  wealth_source: "", nature_of_business: "",
  email: "", mobile: "", country_code: "+91",
  kyc_status: "New",
  is_politically_exposed: false,
});

const blankAddress = () => ({
  address_type: "Residential", address1: "", address2: "", address3: "",
  city: "", state: "", country: "India", pin_code: "",
});

const blankComm = () => ({
  local: blankAddress(),
  foreign: blankAddress(),
  is_same_for_all: true,
  use_guardian_address: false,
});

const blankBank = () => ({
  id: crypto.randomUUID?.() || String(Date.now() + Math.random()),
  bank_name: "", branch: "", account_number: "", ifsc: "",
  account_type: "Savings", micr: "", upi_id: "",
  is_active: true,
});

export default function LeadKycInformationPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMember, setActiveMember] = useState(0);
  const [activeSub, setActiveSub] = useState("profile"); // profile | communication | bank
  const [kyc, setKyc] = useState({}); // { [memberId]: { profile, communication, banks } }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lead-mgmt/${leadId}`);
        setLead(data.data);
        setKyc(data.data?.kyc || {});
      } catch (e) {
        toast.error("Failed to load lead: " + (e?.response?.data?.detail || e.message));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [leadId]);

  const members = useMemo(() => (lead?.introduction?.members || []).map(m => ({
    id: m.member_id || m.name, name: m.name, is_primary: !!m.is_primary,
  })), [lead]);

  const currentMember = members[activeMember];
  const currentKyc = useMemo(() => {
    if (!currentMember) return null;
    const existing = kyc[currentMember.id] || {};
    return {
      profile: { ...blankProfile(), ...(existing.profile || {}) },
      communication: { ...blankComm(), ...(existing.communication || {}) },
      banks: Array.isArray(existing.banks) && existing.banks.length ? existing.banks : [blankBank()],
    };
  }, [kyc, currentMember]);

  const updProfile = (patch) => setKyc(prev => ({
    ...prev,
    [currentMember.id]: {
      ...(prev[currentMember.id] || {}),
      profile: { ...(prev[currentMember.id]?.profile || blankProfile()), ...patch },
    },
  }));
  const updComm = (kind, patch) => setKyc(prev => {
    const cur = prev[currentMember.id]?.communication || blankComm();
    return {
      ...prev,
      [currentMember.id]: {
        ...(prev[currentMember.id] || {}),
        communication: kind === "flags"
          ? { ...cur, ...patch }
          : { ...cur, [kind]: { ...(cur[kind] || blankAddress()), ...patch } },
      },
    };
  });
  const updBanks = (fn) => setKyc(prev => {
    const banks = prev[currentMember.id]?.banks || [blankBank()];
    return {
      ...prev,
      [currentMember.id]: {
        ...(prev[currentMember.id] || {}),
        banks: fn(banks),
      },
    };
  });

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/lead-mgmt/${lead.id}/kyc`, { kyc });
      toast.success("KYC information saved");
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const saveAndNext = async () => {
    await save();
    navigate(`/leads/${leadId}/account-opening`);
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-teal-600" size={32}/></div>;
  if (!lead) return <div className="p-10 text-center text-gray-400">Lead not found.</div>;
  if (!members.length) return (
    <div className="p-10 text-center text-gray-500">
      Add at least one family member in the Introduction stage before capturing KYC.
      <div className="mt-4"><button onClick={() => navigate(`/leads/${leadId}/introduction`)}
        className="text-teal-700 underline">Go to Introduction</button></div>
    </div>
  );

  return (
    <div className="px-4 py-6" data-testid="lead-kyc-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/leads/${leadId}/introduction`)}
            className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 hover:bg-gray-100"
            aria-label="Back"
            data-testid="kyc-back-btn"
          ><ArrowLeft size={16}/></button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">KYC Information</h1>
            <p className="text-xs text-gray-500 mt-0.5">Capture regulatory KYC per family member — {lead.first_name} {lead.last_name}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-4">
        {/* Left panel: member list */}
        <div className="w-56 flex-shrink-0">
          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 px-2 py-2">Family Members</div>
              <div className="space-y-1">
                {members.map((m, i) => (
                  <button
                    key={m.id}
                    onClick={() => setActiveMember(i)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm truncate ${
                      i === activeMember ? "bg-teal-50 text-teal-700 font-semibold" : "hover:bg-gray-50 text-gray-700"
                    }`}
                    data-testid={`kyc-member-${i}`}
                  >
                    {m.name}{m.is_primary && <span className="ml-1 text-[10px] text-teal-600">(Primary)</span>}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right panel */}
        <div className="flex-1 min-w-0">
          <Card className="border-gray-200 shadow-sm">
            <CardContent className="p-6">
              {/* Sub-tabs */}
              <div className="flex items-center gap-2 border-b mb-6">
                {[
                  { key: "profile",       label: "Profile",       icon: User },
                  { key: "communication", label: "Communication", icon: Mail },
                  { key: "bank",          label: "Bank Details",  icon: Building2 },
                ].map(t => {
                  const Icon = t.icon;
                  return (
                    <button key={t.key}
                            onClick={() => setActiveSub(t.key)}
                            className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px ${
                              activeSub === t.key
                                ? "border-teal-600 text-teal-700 font-semibold"
                                : "border-transparent text-gray-500 hover:text-gray-800"
                            }`}
                            data-testid={`kyc-subtab-${t.key}`}
                    ><Icon size={14}/> {t.label}</button>
                  );
                })}
              </div>

              {activeSub === "profile" && (
                <ProfileForm profile={currentKyc.profile} onChange={updProfile}
                             uploadUrl={`/lead-mgmt/${leadId}/files`} api={api} leadId={leadId}/>
              )}
              {activeSub === "communication" && (
                <CommunicationForm comm={currentKyc.communication} onChange={updComm}
                                   uploadUrl={`/lead-mgmt/${leadId}/files`} api={api} leadId={leadId}/>
              )}
              {activeSub === "bank" && (
                <BankForm banks={currentKyc.banks} onChange={updBanks}
                          uploadUrl={`/lead-mgmt/${leadId}/files`} api={api} leadId={leadId}/>
              )}

              <div className="flex items-center justify-end gap-3 pt-4 border-t mt-6">
                <button
                  onClick={save} disabled={busy}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white text-sm px-6 py-2 hover:bg-gray-50 disabled:opacity-60"
                  data-testid="kyc-save-btn"
                >{busy ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Save</button>
                <button
                  onClick={saveAndNext} disabled={busy}
                  className="inline-flex items-center gap-2 rounded-md bg-[#1a7f6f] hover:bg-[#166a5c] text-white text-sm font-semibold px-8 py-2 disabled:opacity-60"
                  data-testid="kyc-savenext-btn"
                >Save & Continue to Account Opening</button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ── Sub-forms ────────────────────────────────────────────────
// Inline file-attach control shared across all KYC sub-forms.
// MOCKED: only file metadata (name/size/mime) is stored today;
// wire Emergent Object Storage before production.
function InlineUpload({ file, onPick, testid, uploadUrl, api, leadId }) {
  const attached = !!file?.name;
  const [busy, setBusy] = useState(false);
  const doUpload = async (f, slot = "kyc") => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { alert("File must be under 5MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`${uploadUrl}?slot=${encodeURIComponent(slot)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onPick(data.data);
    } catch (e) {
      alert("Upload failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1 mt-1">
      <label className={`inline-flex items-center gap-1 text-[11px] cursor-pointer rounded-md border px-2 py-1 ${
        attached ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                 : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
      } ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid={testid}>
        {busy ? <Loader2 size={11} className="animate-spin"/> : (attached ? <Check size={11}/> : <Upload size={11}/>)}
        {attached ? (file.name.length > 22 ? file.name.slice(0, 22) + "…" : file.name) : (busy ? "Uploading…" : "Upload scan")}
        <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
               onChange={e => doUpload(e.target.files?.[0], testid)}/>
      </label>
      {attached && <LeadFileActions file={file} api={api} leadId={leadId} testid={testid}/>}
    </span>
  );
}

function ProfileForm({ profile, onChange, uploadUrl, api, leadId }) {
  const G = ({ label, children, req }) => (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 block">
        {label} {req && <span className="text-rose-500">*</span>}
      </label>
      {children}
    </div>
  );
  const I = (k, extra = {}) => (
    <input value={profile[k] || ""} onChange={e => onChange({ [k]: e.target.value })}
           className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
           data-testid={`kyc-profile-${k}`} {...extra}/>
  );
  const S = (k, options) => (
    <select value={profile[k] || ""} onChange={e => onChange({ [k]: e.target.value })}
            className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
            data-testid={`kyc-profile-${k}`}>
      <option value="">Select</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  const uploads = profile.uploads || {};
  const setUpload = (k, meta) => onChange({ uploads: { ...uploads, [k]: meta } });
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-teal-700 mb-3">Identity</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <G label="PAN Card Number" req>
            {I("pan", { maxLength: 10, placeholder: "AAAAA9999A" })}
            <InlineUpload file={uploads.pan} onPick={m => setUpload("pan", m)} testid="kyc-upload-pan" uploadUrl={uploadUrl} api={api} leadId={leadId}/>
          </G>
          <G label="Aadhaar Number">
            {I("aadhaar", { maxLength: 12, placeholder: "12-digit number" })}
            <InlineUpload file={uploads.aadhaar} onPick={m => setUpload("aadhaar", m)} testid="kyc-upload-aadhaar" uploadUrl={uploadUrl} api={api} leadId={leadId}/>
          </G>
          <G label="CKYC Number">
            {I("ckyc")}
            <InlineUpload file={uploads.ckyc} onPick={m => setUpload("ckyc", m)} testid="kyc-upload-ckyc" uploadUrl={uploadUrl} api={api} leadId={leadId}/>
          </G>
          <G label="Gender">{S("gender", GENDERS)}</G>
          <G label="Place of Birth">{I("place_of_birth")}</G>
          <G label="Country of Birth">
            {I("country_of_birth")}
            <InlineUpload file={uploads.birth_proof} onPick={m => setUpload("birth_proof", m)} testid="kyc-upload-birth" uploadUrl={uploadUrl} api={api} leadId={leadId}/>
          </G>
          <G label="Passport-size Photo">
            <div className="h-9 flex items-center text-xs text-gray-500 px-2 border border-dashed border-gray-300 rounded-md">Photograph</div>
            <InlineUpload file={uploads.photo} onPick={m => setUpload("photo", m)} testid="kyc-upload-photo" uploadUrl={uploadUrl} api={api} leadId={leadId}/>
          </G>
          <G label="Signature">
            <div className="h-9 flex items-center text-xs text-gray-500 px-2 border border-dashed border-gray-300 rounded-md">Signature specimen</div>
            <InlineUpload file={uploads.signature} onPick={m => setUpload("signature", m)} testid="kyc-upload-signature" uploadUrl={uploadUrl} api={api} leadId={leadId}/>
          </G>
        </div>
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-teal-700 mb-3">Occupation & Income</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <G label="Occupation">{S("occupation", OCCUPATIONS)}</G>
          <G label="Employer Name">{I("employer_name")}</G>
          <G label="Nature of Business">{I("nature_of_business")}</G>
          <G label="Gross Annual Income">{S("gross_annual_income", INCOME_SLABS)}</G>
          <G label="Net Worth (INR)">{I("net_worth", { type: "number" })}</G>
          <G label="Net Worth Date">{I("net_worth_date", { type: "date" })}</G>
          <G label="Wealth Source">{S("wealth_source", WEALTH_SOURCES)}</G>
        </div>
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-teal-700 mb-3">Contact & Status</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <G label="Email">{I("email", { type: "email" })}</G>
          <G label="Country Code">{I("country_code", { placeholder: "+91" })}</G>
          <G label="Mobile Number">{I("mobile", { maxLength: 15 })}</G>
          <G label="KYC Status">{S("kyc_status", KYC_STATUSES)}</G>
          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" checked={!!profile.is_politically_exposed}
                   onChange={e => onChange({ is_politically_exposed: e.target.checked })}
                   className="h-4 w-4 accent-teal-600"
                   data-testid="kyc-profile-pep"/>
            <label className="text-sm text-gray-700">Politically Exposed Person</label>
          </div>
        </div>
      </div>
    </div>
  );
}

function CommunicationForm({ comm, onChange, uploadUrl, api, leadId }) {
  const uploads = comm.uploads || {};
  const setUpload = (k, meta) => onChange("flags", { uploads: { ...uploads, [k]: meta } });
  const Addr = ({ kind, addr, title }) => (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-bold uppercase tracking-wider text-teal-700">{title}</h4>
        <InlineUpload file={uploads[`${kind}_address_proof`]}
                      onPick={m => setUpload(`${kind}_address_proof`, m)}
                      testid={`kyc-upload-${kind}-addr`}
                      uploadUrl={uploadUrl} api={api} leadId={leadId}/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FieldSelect label="Address Type" value={addr.address_type}
                     onChange={v => onChange(kind, { address_type: v })}
                     options={ADDRESS_TYPES}
                     testid={`kyc-${kind}-type`}/>
        <FieldInput label="Address Line 1" value={addr.address1}
                    onChange={v => onChange(kind, { address1: v })}
                    testid={`kyc-${kind}-addr1`}/>
        <FieldInput label="Address Line 2" value={addr.address2}
                    onChange={v => onChange(kind, { address2: v })}
                    testid={`kyc-${kind}-addr2`}/>
        <FieldInput label="Address Line 3" value={addr.address3}
                    onChange={v => onChange(kind, { address3: v })}
                    testid={`kyc-${kind}-addr3`}/>
        <FieldInput label="City" value={addr.city}
                    onChange={v => onChange(kind, { city: v })}
                    testid={`kyc-${kind}-city`}/>
        <FieldInput label="State" value={addr.state}
                    onChange={v => onChange(kind, { state: v })}
                    testid={`kyc-${kind}-state`}/>
        <FieldInput label="Country" value={addr.country}
                    onChange={v => onChange(kind, { country: v })}
                    testid={`kyc-${kind}-country`}/>
        <FieldInput label="Pincode" value={addr.pin_code}
                    onChange={v => onChange(kind, { pin_code: v })}
                    testid={`kyc-${kind}-pin`}/>
      </div>
    </div>
  );
  return (
    <div className="space-y-6">
      <Addr kind="local" addr={comm.local} title="Local / Indian Address"/>
      <div className="flex items-center gap-6">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={comm.is_same_for_all}
                 onChange={e => onChange("flags", { is_same_for_all: e.target.checked })}
                 className="h-4 w-4 accent-teal-600"
                 data-testid="kyc-comm-same-all"/>
          Same address for all family members
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={comm.use_guardian_address}
                 onChange={e => onChange("flags", { use_guardian_address: e.target.checked })}
                 className="h-4 w-4 accent-teal-600"
                 data-testid="kyc-comm-use-guardian"/>
          Use guardian address
        </label>
      </div>
      <Addr kind="foreign" addr={comm.foreign} title="Foreign Address (optional)"/>
    </div>
  );
}

function BankForm({ banks, onChange, uploadUrl, api, leadId }) {
  const addBank = () => onChange(bs => [...bs, blankBank()]);
  const removeBank = (id) => onChange(bs => bs.length > 1 ? bs.filter(b => b.id !== id) : bs);
  const updBank = (id, patch) => onChange(bs => bs.map(b => b.id === id ? { ...b, ...patch } : b));

  return (
    <div className="space-y-4">
      {banks.map((b, idx) => (
        <div key={b.id} className="border border-gray-200 rounded-lg p-4"
             data-testid={`kyc-bank-row-${idx}`}>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-teal-700">Bank {idx + 1}</h4>
            <div className="flex items-center gap-2">
              <InlineUpload file={b.cheque_upload}
                            onPick={m => updBank(b.id, { cheque_upload: m })}
                            testid={`kyc-upload-cheque-${idx}`}
                            uploadUrl={uploadUrl} api={api} leadId={leadId}/>
              <button onClick={() => removeBank(b.id)}
                      className="text-rose-600 hover:bg-rose-50 rounded-md p-1.5 disabled:opacity-40"
                      disabled={banks.length === 1}
                      data-testid={`kyc-bank-remove-${idx}`}>
                <Trash2 size={14}/>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FieldInput label="Bank Name" value={b.bank_name} onChange={v => updBank(b.id, { bank_name: v })} testid={`kyc-bank-name-${idx}`}/>
            <FieldInput label="Branch" value={b.branch} onChange={v => updBank(b.id, { branch: v })} testid={`kyc-bank-branch-${idx}`}/>
            <FieldInput label="Account Number" value={b.account_number} onChange={v => updBank(b.id, { account_number: v })} testid={`kyc-bank-acc-${idx}`}/>
            <FieldInput label="IFSC" value={b.ifsc} onChange={v => updBank(b.id, { ifsc: v.toUpperCase() })} testid={`kyc-bank-ifsc-${idx}`}/>
            <FieldSelect label="Account Type" value={b.account_type} onChange={v => updBank(b.id, { account_type: v })}
                         options={BANK_ACCOUNT_TYPES} testid={`kyc-bank-type-${idx}`}/>
            <FieldInput label="MICR" value={b.micr} onChange={v => updBank(b.id, { micr: v })} testid={`kyc-bank-micr-${idx}`}/>
            <FieldInput label="UPI ID" value={b.upi_id} onChange={v => updBank(b.id, { upi_id: v })} testid={`kyc-bank-upi-${idx}`}/>
          </div>
        </div>
      ))}
      <button onClick={addBank}
              className="inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
              data-testid="kyc-bank-add">
        <Plus size={14}/> Add Another Bank
      </button>
    </div>
  );
}

function FieldInput({ label, value, onChange, testid, ...rest }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 block">{label}</label>
      <input value={value || ""} onChange={e => onChange(e.target.value)}
             data-testid={testid}
             className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
             {...rest}/>
    </div>
  );
}
function FieldSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 block">{label}</label>
      <select value={value || ""} onChange={e => onChange(e.target.value)}
              data-testid={testid}
              className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500">
        <option value="">Select</option>
        {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
